import { defineStore } from 'pinia'
import { store } from '../index'
import { SellerUserType } from '@/api/login/types'
import { ElMessageBox } from 'element-plus'
import { useI18n } from '@/hooks/web/useI18n'
import { useTagsViewStore } from './tagsView'
import router from '@/router'
import { SellerAuthInfoType } from '@/views/AccountSecurity/types'
import { UserDetailsInfo } from '@/api/user/types'
import { getDefaultWithdrawalAccountList } from '@/api/wallet'

interface UserState {
  userInfo: SellerUserType | undefined
  roleRouters?: string[] | AppCustomRouteRecordRaw[]
  sellerAuthInfo: SellerAuthInfoType | undefined
  userDetailsInfo: UserDetailsInfo
  isDefaultPay: boolean
  isSafePwd: boolean
  isRealname: boolean
}

export const useUserStore = defineStore('user', {
  state: (): UserState => {
    return {
      userInfo: undefined,
      userDetailsInfo: {} as UserDetailsInfo,
      roleRouters: undefined,
      sellerAuthInfo: undefined,
      isDefaultPay: false,
      isSafePwd: false,
      isRealname: false
    }
  },
  getters: {
    getUserInfo(): SellerUserType | undefined {
      return this.userInfo
    },
    getRoleRouters(): string[] | AppCustomRouteRecordRaw[] | undefined {
      return this.roleRouters
    },
    getUserDetailsInfo(): UserDetailsInfo {
      return this.userDetailsInfo
    },
    getisDefaultPay(): boolean {
      return this.isDefaultPay
    }
  },
  actions: {
    updateUseAvatar(url: string) {
      this.$patch((state) => {
        state.userDetailsInfo.avatar = url
      })
    },
    updateUserDetailsInfo(info: UserDetailsInfo) {
      this.$patch((state) => {
        state.userDetailsInfo = info
      })
    },
    /**@description 更新卖家的认证状态
     * @param status {number}
     * */
    updateAuthStatus(status: number) {
      this.$patch((state) => {
        if (state.sellerAuthInfo) {
          state.sellerAuthInfo.check_status = status
        }
      })
    },
    getSellerAuthInfo() {
      return this.sellerAuthInfo
    },
    /**@description 设置认证信息
     * */
    setSellerAuthInfo(info: SellerAuthInfoType) {
      this.$patch((state) => {
        state.sellerAuthInfo = info
      })
    },
    updateUserData(is_seller: number, email?: string) {
      if (!this.userInfo) return
      this.$patch((state) => {
        if (state.userInfo) {
          state.userInfo.is_seller = is_seller
          state.userInfo.email = email || ''
        }
      })
    },
    getToken() {
      return this.userInfo?.cc_token || ''
    },
    setUserInfo(userInfo: SellerUserType | undefined) {
      this.$patch((state) => {
        state.userInfo = userInfo
      })
    },
    setRoleRouters(roleRouters: string[] | AppCustomRouteRecordRaw[]) {
      this.$patch((state) => {
        state.roleRouters = roleRouters
      })
    },
    setIsDefaultPay(isDefault: boolean): void {
      this.$patch((state) => {
        state.isDefaultPay = isDefault
      })
    },
    setisSafePwd(isSafePwd: boolean): void {
      this.$patch((state) => {
        state.isSafePwd = isSafePwd
      })
    },
    setisRealname(isRealname: boolean): void {
      this.$patch((state) => {
        state.isRealname = isRealname
      })
    },
    logoutConfirm() {
      const { t } = useI18n()
      ElMessageBox.confirm(t('common.loginOutMessage'), t('common.reminder'), {
        confirmButtonText: t('common.ok'),
        cancelButtonText: t('common.cancel'),
        type: 'warning'
      })
        .then(async () => {
          this.reset()
        })
        .catch(() => {})
    },
    reset() {
      const tagsViewStore = useTagsViewStore()
      tagsViewStore.delAllViews()
      this.$patch((state) => {
        state.userInfo = undefined
        state.sellerAuthInfo = undefined
        state.userDetailsInfo = {} as UserDetailsInfo
        state.roleRouters = []
        state.isDefaultPay = false
        state.isRealname = false
        state.isSafePwd = false
      })
      this.setRoleRouters([])
      router.replace('/login')
    },
    logout() {
      this.reset()
    }
  },
  persist: true
})

export const useUserStoreWithOut = () => {
  return useUserStore(store)
}
